'use client'

import { faGavel } from '@fortawesome/pro-solid-svg-icons'
import { useIntl } from 'react-intl'
import { styled } from 'styled-components'

import { useDecodedAccessToken } from '@b-stock/fe-core/bstock-next/components'
import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import {
  AnalyticsEntityType,
  AnalyticsSource,
  useAnalyticsContext,
  Switch,
} from '@b-stock/fe-core/bstock-react'
import { Colors, Typography } from '@b-stock/fe-core/design-system'

import { useListingSearch } from '@/components/listings/search/ListingSearchProvider'
import { FeatureFlags } from '@/helpers/featureFlags'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

import { FiltersButtonIcon } from '../shared/listings-ui'

const ToggleContainer = styled.label<{ $disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.07rem 1rem;
  border-radius: 0.25rem;
  border: 0.0625rem solid ${Colors.Semantic.Neutral.medium_grey};
  line-height: 1rem;
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
`

const ToggleLabel = styled.span`
  ${Typography.Body3}
  color: ${Colors.Semantic.Neutral.black};
  white-space: nowrap;
`

const ScaledSwitch = styled(Switch)`
  transform: scale(0.75);
  transform-origin: center;
`

/**
 * Persistent "My Bids" toggle for the top of the listings page (List & Grid,
 * desktop & mobile). Restricts results to live listings the buyer has an
 * active (winning/losing) bid on. Hidden for logged-out buyers, since the
 * filter is meaningless without an authenticated account.
 */
export const MyBidsFilterToggle = () => {
  const { formatMessage } = useIntl()
  const { trackButtonClicked } = useAnalytics()
  const { screenName } = useAnalyticsContext()
  const { state, updateFilters, myBidsFilter } = useListingSearch()
  const accountId = useDecodedAccessToken()?.accountId
  const isFlagEnabled = useFlag(FeatureFlags.ENABLE_MY_BIDS_LISTINGS_FILTER)

  if (!accountId || !isFlagEnabled) return null

  const isActive = !!state.filters.myBids
  const label = formatMessage({ id: 'Listings.myBidsFilter.toggle' })
  const isDisabled = myBidsFilter.isLoading

  const toggle = (nextValue: boolean) => {
    trackButtonClicked({
      screenName,
      buttonName: 'my_bids_filter_toggled',
      is_selected: nextValue,
      source: AnalyticsSource.HOME_PORTAL,
      entityType: AnalyticsEntityType.BUYER,
    })
    updateFilters({ myBids: nextValue })
  }

  return (
    <ToggleContainer $disabled={isDisabled} title={label}>
      <FiltersButtonIcon icon={faGavel} />
      <ToggleLabel>{label}</ToggleLabel>
      {isDisabled ? (
        <ScaledSwitch a11yLabel={label} value={isActive} disabled />
      ) : (
        <ScaledSwitch a11yLabel={label} value={isActive} onToggle={toggle} />
      )}
    </ToggleContainer>
  )
}
